import React, { useState, useEffect } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';

type HelpPopoverHoverPropsSize = 'small' | 'medium' | 'large';
type HelpPopoverHoverPropsStyle = 'underline' | 'icon';

const isMobileDevice = () => {
  return typeof window !== 'undefined' && window.innerWidth < 768;
};

/**
 * Adds a ShadCN Tooltip or HoverCard depending on what is stored in DB
 *
 * @param children - The content
 * @param config - Configuration object with style and text
 */
export interface HelpPopoverHoverProps {
  config?: {
    content?: string;
    icon?: string;
    size?: string;
    style?: string;
    text?: string;
    cursor?: boolean;
  } | null;
  children?: React.ReactNode;
}

const HelpPopoverHover: React.FC<HelpPopoverHoverProps> = ({ config = null, children }) => {
  // Return children directly if no config or no style/text in config
  if (!config || !config.style || !config.text) {
    return <>{children}</>;
  }
  
  const { icon, size, style, text, cursor } = config;
  console.log('[HelpPopoverHover]', children, config)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if we're on mobile device
    setIsMobile(isMobileDevice());
    
    // Add resize listener to update mobile status
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!style || !text || text == "" ) return <>{children}</>;

  if (style == "icon") {
    console.log('icon mode', config);
    
    if (isMobile) {
      // Mobile implementation using Dialog
      return (
        <Dialog>
          <DialogTrigger asChild>
            <span 
              className={'pl-2 inline-flex items-center'} 
              style={ cursor ? { cursor: 'help'} : undefined }
            >
              <FontAwesomeIcon icon={faCircleQuestion} className="h-3.5 w-3.5 -mt-0.5" />
            </span>
          </DialogTrigger>
          <DialogContent className="w-80 max-w-[calc(100vw-2rem)] top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 bg-card">
            <DialogHeader>
              <DialogTitle>Help Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-1">
              <p className="text-sm">
                {text}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      );
    }
    
    // Desktop implementation using HoverCard
    return (
      <HoverCard openDelay={750}>
        <HoverCardTrigger asChild>
          <span className={'pl-2 inline-flex items-center'} style={ cursor ? { cursor: 'help'} : undefined }>
            <FontAwesomeIcon icon={faCircleQuestion} className="h-3.5 w-3.5 -mt-0.5" />
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 bg-card">
          <div className="flex justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm">
                {text}
              </p>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    )
  };

  if (style == 'underline') {
    console.log('underline mode', config);
    
    if (isMobile) {
      // Mobile implementation using Dialog
      return (
        <Dialog>
          <DialogTrigger asChild>
            <span style={{
              ...(config.cursor ? { cursor: 'help' } : {}),
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            }}
            >{children}</span>
          </DialogTrigger>
          <DialogContent className="w-80 max-w-[calc(100vw-2rem)] top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2">
            <DialogHeader>
              <DialogTitle>Help Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-1">
              <p className="text-sm">
                {text}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      );
    }
    
    // Desktop implementation using HoverCard
    return (
      <HoverCard openDelay={750}>
        <HoverCardTrigger asChild>
          <span style={{
            ...(config.cursor ? { cursor: 'help' } : {}),
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
          }}
          >{children}</span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80">
          <div className="flex justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm">
                {text}
              </p>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    )
  };

  return <>{children}</>;

};

export default HelpPopoverHover;
